import { Modal, Select, Button, Stack, Group, Text } from '@mantine/core';
import { useForm } from '@mantine/form';
import { usePeople } from '@/hooks/api/usePeople';
import { useOrganization } from '@/hooks/useOrganization';
import type { Asset } from '@/types/models';

interface AssignAssetModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (personId: string) => void;
  asset: Asset | null;
  isLoading?: boolean;
}

export function AssignAssetModal({
  opened,
  onClose,
  onSubmit,
  asset,
  isLoading,
}: AssignAssetModalProps) {
  const { organizationId } = useOrganization();
  const { data: people = [] } = usePeople(organizationId);

  const form = useForm({
    initialValues: {
      personId: '',
    },
    validate: {
      personId: (value) => (!value ? 'Please select a person' : null),
    },
  });

  const handleSubmit = (values: { personId: string }) => {
    onSubmit(values.personId);
    form.reset();
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const peopleOptions = people.map((person) => ({
    value: person.id,
    label: `${person.full_name}${person.email ? ` (${person.email})` : ''}`,
  }));

  return (
    <Modal opened={opened} onClose={handleClose} title="Assign Asset to Person" size="md">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {asset && (
            <div>
              <Text size="sm" c="dimmed" mb="xs">
                Asset
              </Text>
              <Text fw={500}>
                {asset.tag_id} - {asset.description}
              </Text>
              <Text size="sm" c="dimmed">
                {asset.brand} {asset.model}
              </Text>
            </div>
          )}

          <Select
            label="Assign to"
            placeholder="Select a person"
            required
            data={peopleOptions}
            searchable
            {...form.getInputProps('personId')}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" loading={isLoading}>
              Assign Asset
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
